<template>
    <v-container fluid class="bg-grey-lighten-5 pt-16 mt-8 pb-16 h-100">
        <v-row justify="center">
            <v-col cols="12" md="11" xl="10">
                
                <!-- Header de Página -->
                <div class="d-flex align-center justify-space-between mb-8 px-2">
                    <div>
                        <h1 class="text-h4 font-weight-black text-grey-darken-3">Mi Perfil</h1>
                        <p class="text-subtitle-1 text-medium-emphasis mt-1">Gestiona tu información personal y seguridad</p>
                    </div>
                    <div class="d-none d-sm-block">
                        <v-chip color="primary" variant="tonal" class="font-weight-bold" size="large">
                            <v-icon start>fa:fa-solid fa-id-badge</v-icon>
                            {{ userLogin.role_user?.name || 'Usuario' }}
                        </v-chip>
                    </div>
                </div>

                <v-form v-model="validateForm">
            <v-row>
                        <!-- COLUMNA IZQUIERDA: Tarjeta de Identidad (Sticky) -->
                        <v-col cols="12" md="4" lg="3">
                            <v-card elevation="0" class="rounded-xl overflow-visible bg-white border-0 shadow-soft sticky-top">
                                <!-- Fondo decorativo superior -->
                                <div class="profile-cover rounded-t-xl"></div>
                                
                                <div class="px-6 pb-6 text-center" style="margin-top: -60px;">
                                    <div class="d-inline-block position-relative">
                                        <div class="avatar-glow rounded-circle">
                                            <v-avatar size="120" class="border-4 border-white elevation-2 bg-white">
                                                <img v-if="userLogin.avatar" :src="userLogin.avatar" style="object-fit: cover; width: 100%; height: 100%;">
                                                <span v-else class="text-h3 font-weight-bold text-primary">{{ initialName(userLogin.nombres + ' ' + userLogin.apellidos) }}</span>
                            </v-avatar>
                                        </div>
                                    </div>

                                    <h2 class="text-h6 font-weight-bold mt-4 text-grey-darken-4">
                                        {{ capitalizeWords(userLogin.nombres) }}
                                    </h2>
                                    <div class="text-caption font-weight-bold text-uppercase text-primary letter-spacing-1 mb-1">
                                        {{ capitalizeWords(userLogin.apellidos) }}
                                    </div>
                                    <div class="text-body-2 text-medium-emphasis mb-4">
                                        {{ capitalizeWords(userLogin.cargo || userLogin.carrera || 'Sin cargo') }}
                                    </div>

                                    <v-divider class="mb-4"></v-divider>

                                    <div class="d-flex flex-column gap-2 text-left">
                                        <div class="d-flex align-center text-caption text-grey-darken-1">
                                            <v-icon size="small" style="color: #002B49;" class="mr-3">fa:fa-solid fa-envelope</v-icon>
                                            <span class="text-truncate">{{ userLogin.email }}</span>
                                        </div>
                                        <div class="d-flex align-center text-caption text-grey-darken-1">
                                            <v-icon size="small" style="color: #002B49;" class="mr-3">fa:fa-solid fa-mobile-screen</v-icon>
                                            <span>{{ user.celular || 'No registrado' }}</span>
                                        </div>
                                    </div>
                                </div>
                            </v-card>
                            
                            <!-- Botón de Guardar en Columna Izq para pantallas grandes -->
                            <div class="d-none d-md-block mt-6">
                                <v-btn v-if="user.iduser" :disabled="!validateForm" @click="updateUserWithReload(true)"
                                    height="56" class="rounded-xl text-white font-weight-bold text-body-1 shadow-colored"
                                    block color="primary">
                                    Guardar Cambios
                                </v-btn>
                            </div>
                </v-col>

                        <!-- COLUMNA DERECHA: Bloques de Formulario -->
                        <v-col cols="12" md="8" lg="9">
                            
                            <!-- Bloque 1: Datos Personales -->
                            <v-card elevation="0" class="rounded-xl pa-6 mb-6 border-0 shadow-card bg-white">
                                <div class="d-flex align-center mb-6">
                                    <v-avatar color="blue-lighten-5" class="mr-3 rounded-lg" size="40">
                                        <v-icon style="font-size: 20px; color: #1976D2;">fa:fa-solid fa-id-card</v-icon>
                                    </v-avatar>
                                    <div>
                                        <h3 class="text-h6 font-weight-bold text-grey-darken-3">Información Personal</h3>
                                        <div class="text-caption text-medium-emphasis">Datos básicos de identificación</div>
                                    </div>
                                </div>

                                <v-row dense>
                                    <v-col cols="12" md="6">
                                        <v-select label="Tipo Documento" variant="outlined" density="comfortable" color="primary"
                                            v-model="user.tipo_documento" :items="['DNI', 'Carnet de Extranjería']"
                                            bg-color="grey-lighten-5" hide-details="auto" class="mb-4" :rules="[rules.required]">
                                            <template v-slot:prepend-inner>
                                                <v-icon size="small" class="mr-2">fa:fa-solid fa-id-card</v-icon>
                                            </template>
                                        </v-select>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field label="Número Documento" variant="outlined" density="comfortable" color="primary"
                                            v-model="user.numero_documento" @change="getDNIColaborador()"
                                            bg-color="grey-lighten-5" hide-details="auto" class="mb-4" :rules="[rules.required]">
                                            <template v-slot:prepend-inner>
                                                <v-icon size="small" class="mr-2">fa:fa-solid fa-fingerprint</v-icon>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field label="Nombres" variant="outlined" density="comfortable" color="primary"
                                            v-model="user.nombres" bg-color="grey-lighten-5" hide-details="auto" class="mb-4"
                                            :rules="[rules.required]">
                                            <template v-slot:prepend-inner>
                                                <v-icon size="small" class="mr-2">fa:fa-solid fa-user</v-icon>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field label="Apellidos" variant="outlined" density="comfortable" color="primary"
                                            v-model="user.apellidos" bg-color="grey-lighten-5" hide-details="auto" class="mb-4"
                                            :rules="[rules.required]">
                                            <template v-slot:prepend-inner>
                                                <v-icon size="small" class="mr-2">fa:fa-solid fa-user-tag</v-icon>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                </v-row>
                            </v-card>

                            <!-- Bloque 2: Institucional (Condicional) -->
                            <v-card v-if="user.tipouser == 'colaborador' || user.tipouser == 'estudiante'" elevation="0" class="rounded-xl pa-6 mb-6 border-0 shadow-card bg-white">
                                <div class="d-flex align-center mb-6">
                                    <v-avatar color="purple-lighten-5" class="mr-3 rounded-lg" size="40">
                                        <v-icon style="font-size: 18px; color: #7B1FA2;">fa:fa-solid fa-building-columns</v-icon>
                                    </v-avatar>
                                    <div>
                                        <h3 class="text-h6 font-weight-bold text-grey-darken-3">Información Institucional</h3>
                                        <div class="text-caption text-medium-emphasis">Detalles de tu rol en la organización</div>
                                    </div>
                                </div>

                                <v-row dense>
                                    <template v-if="user.tipouser == 'colaborador'">
                                        <v-col cols="12" md="6">
                                            <v-text-field label="Cargo" variant="outlined" density="comfortable" color="primary"
                                                v-model="user.cargo" bg-color="grey-lighten-5" hide-details="auto" class="mb-4"
                                                :rules="[rules.required]">
                                                <template v-slot:prepend-inner>
                                                    <v-icon size="small" class="mr-2">fa:fa-solid fa-briefcase</v-icon>
                                                </template>
                                            </v-text-field>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field label="Área" variant="outlined" density="comfortable" color="primary"
                                                v-model="user.area" bg-color="grey-lighten-5" hide-details="auto" class="mb-4"
                                                :rules="[rules.required]">
                                                <template v-slot:prepend-inner>
                                                    <v-icon size="small" class="mr-2">fa:fa-solid fa-building</v-icon>
                                                </template>
                                            </v-text-field>
                                        </v-col>
                                    </template>
                                    <template v-if="user.tipouser == 'estudiante'">
                                        <v-col cols="12" md="6">
                                            <v-text-field label="Carrera" variant="outlined" density="comfortable" color="primary"
                                                v-model="user.carrera" bg-color="grey-lighten-5" hide-details="auto" class="mb-4"
                                                :rules="[rules.required]">
                                                <template v-slot:prepend-inner>
                                                    <v-icon size="small" class="mr-2">fa:fa-solid fa-graduation-cap</v-icon>
                                                </template>
                                            </v-text-field>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field label="Admisión" variant="outlined" density="comfortable" color="primary"
                                                v-model="user.admision" bg-color="grey-lighten-5" hide-details="auto" class="mb-4"
                                                :rules="[rules.required]">
                                                <template v-slot:prepend-inner>
                                                    <v-icon size="small" class="mr-2">fa:fa-solid fa-calendar</v-icon>
                                                </template>
                                            </v-text-field>
                                    </v-col>
                                    </template>
                                </v-row>
                            </v-card>

                            <!-- Bloque 3: Contacto y Seguridad -->
                            <v-card elevation="0" class="rounded-xl pa-6 mb-6 border-0 shadow-card bg-white">
                                <div class="d-flex align-center mb-6">
                                    <v-avatar color="teal-lighten-5" class="mr-3 rounded-lg" size="40">
                                        <v-icon style="font-size: 18px; color: #00796B;">fa:fa-solid fa-user-shield</v-icon>
                                    </v-avatar>
                                    <div>
                                        <h3 class="text-h6 font-weight-bold text-grey-darken-3">Contacto y Seguridad</h3>
                                        <div class="text-caption text-medium-emphasis">Medios de contacto y acceso</div>
                                    </div>
                                </div>

                                <v-row dense>
                                    <v-col cols="12">
                                        <v-text-field label="Correo Electrónico" variant="outlined" density="comfortable" color="primary"
                                            v-model="user.email" bg-color="grey-lighten-5" hide-details="auto" class="mb-4"
                                            :rules="[rules.required, rules.email]">
                                            <template v-slot:prepend-inner>
                                                <v-icon size="small" class="mr-2">fa:fa-solid fa-envelope</v-icon>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field label="Celular" variant="outlined" density="comfortable" color="primary"
                                            v-model="user.celular" bg-color="grey-lighten-5" hide-details="auto" class="mb-4"
                                            :rules="[rules.required]">
                                            <template v-slot:prepend-inner>
                                                <v-icon size="small" class="mr-2">fa:fa-solid fa-mobile-screen</v-icon>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field label="Whatsapp" variant="outlined" density="comfortable" color="success"
                                            v-model="user.whatsapp" bg-color="grey-lighten-5" hide-details="auto" class="mb-4">
                                            <template v-slot:prepend-inner>
                                                <v-icon size="small" class="mr-2">fa:fa-brands fa-whatsapp</v-icon>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12">
                                        <v-text-field label="Nueva Contraseña" :type="hidepass ? 'password' : 'text'" variant="outlined"
                                            density="comfortable" color="primary" v-model="user.password"
                                            bg-color="grey-lighten-5" hide-details="auto" class="mb-2"
                                            placeholder="Solo escribe si deseas cambiarla">
                                            <template v-slot:prepend-inner>
                                                <v-icon size="small" class="mr-2">fa:fa-solid fa-lock</v-icon>
                                            </template>
                                            <template v-slot:append-inner>
                                                <v-icon style="cursor: pointer;" @click="hidepass = !hidepass" color="medium-emphasis" size="small">
                                                    {{ hidepass ? 'fa:fa-solid fa-eye-slash' : 'fa:fa-solid fa-eye' }}
                                                </v-icon>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                </v-row>
                            </v-card>

                            <!-- Botón Móvil -->
                            <div class="d-md-none mt-4">
                                    <v-btn v-if="user.iduser" :disabled="!validateForm" @click="updateUserWithReload(true)"
                                    height="50" class="rounded-xl text-white font-weight-bold"
                                    block color="primary" elevation="4">
                                    Guardar Cambios
                                </v-btn>
                            </div>

                                </v-col>
                    </v-row>
                        </v-form>
                </v-col>
            </v-row>
    </v-container>
</template>

<script>
import Filters from '@/modules/Shared/Filters';
import { mapState, mapMutations, mapActions } from 'vuex';
export default
    {
        mixins: [Filters],
        data() {
            return {
                validateForm: true,
                hidepass: true
            }
        },
        computed:
        {
            ...mapState({ userLogin: 'user' }),
            ...mapState("usuarios", [
                "tiposUsuario",
                "dialogEdit",
                'user',
            ]),
            ...mapState('usuarios/roles', ['listrole', 'loading']),
        },

        methods: {
            ...mapMutations('usuarios', ['pushUser']),
            ...mapActions('usuarios', ['updateUserWithReload'])
        },

        mounted() {
            this.pushUser(this.userLogin)
        }
    }
</script>

<style scoped>
.profile-cover {
    height: 100px;
    background: linear-gradient(135deg, #0F172A 0%, #1e293b 100%);
    position: relative;
    overflow: hidden;
}

.profile-cover::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('https://grainy-gradients.vercel.app/noise.svg');
    opacity: 0.1;
}

.shadow-soft {
    box-shadow: 0 10px 30px -10px rgba(0,0,0,0.08) !important;
}

.shadow-card {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.02) !important;
    border: 1px solid rgba(0,0,0,0.03) !important;
}

.shadow-colored {
    box-shadow: 0 10px 25px -5px rgba(99, 102, 241, 0.4) !important;
}

.avatar-glow {
    position: relative;
    z-index: 2;
}

.sticky-top {
    position: sticky;
    top: 100px; /* Ajustar según la altura del navbar */
    z-index: 1;
}

.letter-spacing-1 {
    letter-spacing: 1px;
}
</style>